/**
 * Auth facade — the app's single entry point for identity.
 *
 * Every component imports `useAuth0` from "ct-auth" (a webpack alias set in
 * craco.config.js) instead of from "@auth0/auth0-react". The exported shape is
 * IDENTICAL to the Auth0 SDK's, so the migration across ~159 files was a pure import
 * rewrite with no call-site changes.
 *
 * Behind it sit three sources of identity, checked in this order:
 *
 *   1. Player OTP session  — a server-minted token in sessionStorage. Checked FIRST
 *      because a player who signed in with a code has no IdP session at all; if the
 *      IdP adapter ran first it would report isAuthenticated:false and bounce them
 *      back to the login page.
 *   2. Keycloak (OIDC)     — via react-oidc-context, when REACT_APP_AUTH_PROVIDER=keycloak.
 *   3. Auth0               — the default, unchanged.
 *
 * The provider is resolved at RUNTIME (see runtime-config.js), so one image can serve
 * either IdP.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useSyncExternalStore } from "react"
import { useAuth0 as useAuth0Sdk, withAuthenticationRequired as auth0WithAuthenticationRequired } from "@auth0/auth0-react"
import { useAuth as useOidcAuth } from "react-oidc-context"

import { isKeycloak } from "./runtime-config"
import { getPlayerSession, peekPlayerSession, subscribePlayerSession, clearPlayerSession } from "../../data-utils/playerSessionStore"

export { AuthProvider } from "./AuthProvider"

/**
 * Explicit override for the whole auth state.
 *
 * The bot screen-share board (GameplayDisplay) renders the normal gameplay tree with a
 * synthetic "logged-in bot" identity and a pre-minted token. It used to do that by
 * providing the Auth0 SDK's own context, which only works while Auth0 is the provider.
 * This context is provider-agnostic, and useAuth0() checks it before anything else.
 *
 * Exported as `Auth0Context` too, so the existing call site needs no change.
 */
export const AuthOverrideContext = createContext(null)
export const Auth0Context = AuthOverrideContext
export * from "./claims"
export * from "./runtime-config"

/**
 * Build an Auth0-SDK-shaped object from a player OTP session.
 *
 * The synthetic `user` carries the Keycloak-style flat claims so the normalisers in
 * claims.js resolve it the same way they resolve a real token's claims — the rest of
 * the app cannot tell the difference, and nothing needs a special case for players.
 *
 * REFERENCE STABILITY IS LOAD-BEARING. Many components put `user` and
 * `getAccessTokenSilently` in a useEffect dependency array (Exercises.jsx calls
 * registerUser that way). Returning a fresh object literal on every render makes those
 * deps change every render, so the effect re-runs, sets state, re-renders, and fires
 * again — an infinite request loop that only shows up in a browser, never in a unit
 * test. The Auth0 SDK memoises its context value; this must too, hence the useMemo in
 * usePlayerSessionAuthState below.
 */
const buildPlayerSessionAuthState = (session, onLogout) => ({
  user: {
    sub: session.userId,
    email: session.email,
    name: session.email,
    picture: null,
    ct_allowed_roles: ["user"],
    ct_default_role: "user",
    ct_org_id: session.orgId ?? "",
    ct_permissions: "",
    ct_auth_plane: "otp",
  },
  isAuthenticated: true,
  isLoading: false,
  error: undefined,
  // authHeadersFor puts this on X-CT-Player-JWT rather than Authorization; returning
  // it here keeps every existing `await getAccessTokenSilently()` call site working.
  getAccessTokenSilently: async () => session.token,
  getAccessTokenWithPopup: async () => session.token,
  getIdTokenClaims: async () => ({ __raw: session.token }),
  // A player session has no IdP session to redirect into. Clearing it returns the
  // user to the app's own login screen, which is where the OTP form lives.
  loginWithRedirect: async () => {
    onLogout()
  },
  loginWithPopup: async () => {
    onLogout()
  },
  handleRedirectCallback: async () => ({ appState: {} }),
  logout: () => {
    onLogout()
  },
})

// react-oidc-context's default context value is `undefined` when no AuthProvider is
// mounted — unlike the Auth0 SDK, which returns a populated default. Since useAuth0()
// must call BOTH adapter hooks unconditionally (rules of hooks), the Keycloak adapter
// has to tolerate that. It happens in two real situations: components rendered in unit
// tests without a provider, and any Auth0-mode page, where the OIDC provider is never
// mounted at all.
const NO_OIDC_SESSION = Object.freeze({
  user: undefined,
  isAuthenticated: false,
  isLoading: false,
  error: undefined,
  signinRedirect: async () => {},
  signinPopup: async () => {},
  signinSilent: async () => null,
  signoutRedirect: async () => {},
})

/**
 * Memoised player-session auth state.
 *
 * Keyed on the session token: while the token is unchanged the identity of `user` and
 * of every returned function stays stable, so dependency arrays across the app settle
 * after one render.
 */
const usePlayerSessionAuthState = (session, onLogout) =>
  useMemo(
    () => (session ? buildPlayerSessionAuthState(session, onLogout) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.token, session?.userId, session?.email, session?.orgId, session?.expiresAt, onLogout]
  )

// One refresh at a time, shared by every caller. The realm rotates refresh tokens
// (bootstrap.mjs ensureRefreshTokenRotation), so a second refresh sent with the same
// token is refused with 400 invalid_grant — which is what several components asking for
// a token at once after expiry used to cause.
let renewal = null
let redirectingToLogin = false

const renewAccessToken = (oidc) => {
  renewal ??= Promise.resolve(oidc.signinSilent?.())
    .catch(() => null)
    .finally(() => {
      renewal = null
    })
  return renewal
}

/** Map react-oidc-context's shape onto the Auth0 SDK's. */
const useKeycloakAuthState = () => {
  const oidc = useOidcAuth() ?? NO_OIDC_SESSION
  // Read through a ref so getAccessTokenSilently keeps ONE identity. Every signinSilent
  // changes the OIDC context state; a callback keyed on it re-ran each effect that lists
  // it as a dependency, which asked for another token — a refresh/re-render loop that
  // made the page flicker (see the REFERENCE STABILITY note above).
  const oidcRef = useRef(oidc)
  oidcRef.current = oidc

  const getAccessTokenSilently = useCallback(async () => {
    const current = oidcRef.current
    // oidc-client-ts renews in the background via silent renew; if the stored user is
    // already expired, force one round trip rather than handing back a dead token.
    if (current.user && !current.user.expired) return current.user.access_token
    const renewed = await renewAccessToken(current)
    if (renewed?.access_token) return renewed.access_token
    // The refresh token is spent or the session ended. Retrying cannot help, so drop the
    // dead session and sign in again — once, however many callers are waiting. Keycloak's
    // SSO cookie usually makes that an instant round trip.
    if (current.user && !redirectingToLogin) {
      redirectingToLogin = true
      await current.removeUser?.().catch(() => {})
      await current.signinRedirect?.({ state: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` } })
    }
    throw new Error("Unable to obtain an access token")
  }, [])

  // Keyed on the stored user, not the whole context: navigator/error updates during a
  // renewal must not hand components a new `user` (same loop as above).
  const oidcUser = oidc.user
  const user = useMemo(() => (oidcUser ? { ...oidcUser.profile, sub: oidcUser.profile?.sub, email: oidcUser.profile?.email } : undefined), [oidcUser])

  return useMemo(
    () => ({
      // `profile` holds the ID-token claims; spreading `sub`/`email` to the top level
      // matches what the Auth0 SDK's `user` object looks like.
      user,
      isAuthenticated: oidc.isAuthenticated,
      isLoading: oidc.isLoading,
      error: oidc.error,
      getAccessTokenSilently,
      getAccessTokenWithPopup: getAccessTokenSilently,
      getIdTokenClaims: async () => {
        const current = oidcRef.current.user
        return current ? { ...current.profile, __raw: current.id_token } : undefined
      },
      loginWithRedirect: async (opts = {}) => {
        // Auth0 puts caller state under `appState`; oidc-client-ts calls it `state`.
        await oidcRef.current.signinRedirect?.({
          state: opts.appState,
          login_hint: opts.authorizationParams?.login_hint,
          prompt: opts.authorizationParams?.prompt,
        })
      },
      loginWithPopup: async () => {
        await oidcRef.current.signinPopup?.()
      },
      handleRedirectCallback: async () => ({ appState: oidcRef.current.user?.state ?? {} }),
      logout: (opts = {}) => {
        oidcRef.current.signoutRedirect?.({
          post_logout_redirect_uri: opts.logoutParams?.returnTo ?? window.location.origin,
        })
      },
    }),
    [user, oidc.isAuthenticated, oidc.isLoading, oidc.error, getAccessTokenSilently]
  )
}

/**
 * Drop-in replacement for the Auth0 SDK's useAuth0().
 *
 * NOTE ON HOOK ORDER: both underlying hooks are called unconditionally on every
 * render. Branching on the provider *before* calling them would violate the rules of
 * hooks the moment runtime config changed. The inactive adapter must therefore
 * tolerate having no provider mounted: the Auth0 SDK returns a populated default
 * context, but react-oidc-context returns `undefined`, which is why the Keycloak
 * adapter falls back to NO_OIDC_SESSION.
 */
export const useAuth0 = () => {
  const override = useContext(AuthOverrideContext)
  const playerSession = useSyncExternalStore(subscribePlayerSession, peekPlayerSession, () => null)

  const onPlayerLogout = useCallback(() => {
    clearPlayerSession()
    window.location.assign(`${window.location.origin}${process.env.PUBLIC_URL ?? "/"}`)
  }, [])

  // Re-read through getPlayerSession so an EXPIRED entry is treated as absent and the
  // user falls through to the IdP adapter (and thence to the login screen).
  const activeSession = playerSession ? getPlayerSession() : null

  // All three hooks run unconditionally, before any branch — the rules of hooks do not
  // allow an early return above a hook call.
  const player = usePlayerSessionAuthState(activeSession, onPlayerLogout)
  const keycloak = useKeycloakAuthState()
  const auth0 = useAuth0Sdk()

  // An explicit override wins over everything — it is only ever set by a deliberate
  // provider in the tree (the bot display board).
  if (override) return override
  if (player) return player

  return isKeycloak() ? keycloak : auth0
}

/** Alias for new code; identical behaviour. */
export const useAuth = useAuth0

/**
 * Route guard. On Keycloak the OIDC provider handles the redirect itself, so the
 * wrapper only needs to hold rendering until the session resolves.
 */
export const withAuthenticationRequired = (Component, options) => {
  if (!isKeycloak()) return auth0WithAuthenticationRequired(Component, options)
  const Guarded = (props) => {
    const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0()
    if (isLoading) return options?.onRedirecting?.() ?? null
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}` } })
      return options?.onRedirecting?.() ?? null
    }
    return <Component {...props} />
  }
  Guarded.displayName = `withAuthenticationRequired(${Component.displayName || Component.name || "Component"})`
  return Guarded
}
